import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { Office365LinkedService } from 'app/model/linkedservice/office365.linkedservice';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { GlobalVariables } from 'app/services/globals';
import { Credential, office365AuthTypes } from 'shell-models-lib';
import { ReferenceAdapter } from '../reference.adapter';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Office365LinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                office365SubscriptionId: '*',
                servicePrincipalTenantId: '*',
                office365TenantId: '*',
                servicePrincipalId: '*',
                servicePrincipalCredentialType: '*',
                servicePrincipalKey: 'secret',
                servicePrincipalEmbeddedCert: 'secret',
                servicePrincipalEmbeddedCertPassword: 'secret',
                useOAuth: '*',
                encryptedCredential: '*',
                credential: GlobalVariables.enableConnectorUserAuthCred ? '*' : undefined
            }
        }
    }
})
export class Office365LinkedServiceAdapter extends LinkedServiceAdapter<Office365LinkedService> {
    protected modelConstructor(): new () => Office365LinkedService {
        return Office365LinkedService;
    }

    protected model(): IModel {
        return Office365LinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: Office365LinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.Office365LinkedService;
        properties.type = Metamodel.modelFor(Office365LinkedService).name;

        const tenantId = linkedService.office365TenantId;
        let servicePrincipalTenantId;
        let servicePrincipalId;
        let servicePrincipalKey;
        let servicePrincipalCredentialType: string;
        let servicePrincipalEmbeddedCert: string | Model.SecretBase;
        let servicePrincipalEmbeddedCertPassword: string | Model.SecretBase;
        let credential;
        if (GlobalVariables.enableConnectorUserAuthCred) {
            if (linkedService.authenticationType === office365AuthTypes.ServicePrincipal) {
                servicePrincipalTenantId = tenantId;
                servicePrincipalId = linkedService.servicePrincipalId;
                servicePrincipalCredentialType =
                    linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert
                        ? servicePrincipalCredentialTypes.ServicePrincipalCert
                        : undefined;
                if (servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                    servicePrincipalEmbeddedCert = parameterizablePasswordOrAKVtoSecretBase(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, this.context);
                    servicePrincipalEmbeddedCertPassword = parameterizablePasswordOrAKVtoSecretBase(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, this.context);
                } else {
                    servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_KEY, this.context);
                }
            } else if (linkedService.authenticationType === office365AuthTypes.UserAuth) {
                credential = linkedService.credential
                    ? ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference
                    : undefined;
            }
        } else {
            servicePrincipalTenantId = tenantId;
            servicePrincipalId = linkedService.servicePrincipalId;
            servicePrincipalCredentialType = linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert
                ? servicePrincipalCredentialTypes.ServicePrincipalCert
                : undefined;
            if (servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                servicePrincipalEmbeddedCert = parameterizablePasswordOrAKVtoSecretBase(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, this.context);
                servicePrincipalEmbeddedCertPassword = parameterizablePasswordOrAKVtoSecretBase(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, this.context);
            } else {
                servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_KEY, this.context);
            }
        }

        properties.typeProperties = {
            office365TenantId: tenantId,
            servicePrincipalTenantId,
            servicePrincipalId,
            servicePrincipalKey,
            servicePrincipalCredentialType,
            servicePrincipalEmbeddedCert,
            servicePrincipalEmbeddedCertPassword,
            credential
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: Office365LinkedService): Promise<Office365LinkedService> {
        const properties = linkedServiceResource.properties as Model.Office365LinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.Office365LinkedServiceTypeProperties = properties.typeProperties;

        linkedService.office365TenantId = typeProperties.office365TenantId;

        if (GlobalVariables.enableConnectorUserAuthCred) {
            if (typeProperties.servicePrincipalId) {
                linkedService.authenticationType = office365AuthTypes.ServicePrincipal;
                linkedService.servicePrincipalTenantId = typeProperties.servicePrincipalTenantId;
                linkedService.servicePrincipalId = typeProperties.servicePrincipalId;
                linkedService.servicePrincipalCredentialType = typeProperties.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert
                    ? servicePrincipalCredentialTypes.ServicePrincipalCert
                    : servicePrincipalCredentialTypes.ServicePrincipalKey;
                if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                    parameterizableSecretBaseToModel(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, typeProperties.servicePrincipalEmbeddedCert, this.context, !typeProperties.credential);
                    parameterizableSecretBaseToModel(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, typeProperties.servicePrincipalEmbeddedCertPassword, this.context, !typeProperties.credential);
                } else {
                    parameterizableSecretBaseToModel(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context, !typeProperties.credential);
                }
            } else if (typeProperties.credential) {
                linkedService.authenticationType = office365AuthTypes.UserAuth;
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
            }
        } else {
            linkedService.servicePrincipalTenantId = typeProperties.servicePrincipalTenantId;
            linkedService.servicePrincipalId = typeProperties.servicePrincipalId;
            linkedService.servicePrincipalCredentialType = typeProperties.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert
                ? servicePrincipalCredentialTypes.ServicePrincipalCert
                : servicePrincipalCredentialTypes.ServicePrincipalKey;
            if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                parameterizableSecretBaseToModel(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, typeProperties.servicePrincipalEmbeddedCert, this.context, !typeProperties.credential);
                parameterizableSecretBaseToModel(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, typeProperties.servicePrincipalEmbeddedCertPassword, this.context, !typeProperties.credential);
            } else {
                parameterizableSecretBaseToModel(linkedService, Office365LinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context, !typeProperties.credential);
            }
        }

        return linkedService;
    }
}
